import { Grid, Stack, Typography, Chip, Box, Divider, useTheme,Button } from '@mui/material';
import { Logo } from 'ui-component';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import { useYupValidationResolver } from 'hooks/yupValidation/useYupValidationResolver';
import { useTransactionSubmittionData } from './hooks/data/useTransactionSubmittionData';
import PaymentDetails from './paymentDetails';
import colors from 'assets/scss/_themes-vars.module.scss';
import { useTransactionPaymentValidation } from 'utils/validation/payments/transaction-payment-submittion';
import { PaymentMethod } from 'enums';
import StatusAlert from './paymentStatus';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from './errorFallback';
import Spacer from 'ui-component/Spacer';
import { format } from 'date-fns';
import { PaymentMethodSelector, PaymentMethodProvider, usePaymentMethod } from './paymentMethod';
import PaymentMethodBank from './paymentMethod/byBank';
import PaymentMethodCard from './paymentMethod/byCard';
import PageUnderConstruction from 'views/PageUnderConstruction';
import ReCAPTCHA from 'react-google-recaptcha';
import { useRef, useEffect } from 'react';
import { useSettings } from 'hooks/useSettings';
import { isNil } from 'lodash';
import { ProjectTables } from 'enums';
import { useTokenDecode } from './hooks/data/useTokenDecode';
import { FileProvider, useFileContext } from 'context/FilesV2';
import { formatFiles, downloadFile, uploadFiles, deleteFile, deleteBundle } from 'utils';
import { transaction } from 'apis';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { auth } from 'apis';

import { useSelector } from 'react-redux';

const recaptchaKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
const PaymentSubmittion = () => {
    const { transactionDetailsQuery, filesQuery, token } = useTransactionSubmittionData();
    const { transactionId } = useTokenDecode();

    return (
        <FileProvider
            downloadFile={(fileId, type) => downloadFile(fileId, type)}
            uploadFiles={(fileGroups) => uploadFiles(fileGroups, token)}
            onDeleteBundle={async (bundleId) => await deleteBundle(bundleId, ProjectTables.TransHeader, null, transactionId, token)}
            onDeleteFile={async (fileId) => await deleteFile(fileId, ProjectTables.TransHeader, null, transactionId, token)}
        >
            <PaymentMethodProvider>
                <ErrorBoundary FallbackComponent={ErrorFallback} onError={() => {}}>
                    <PaymentSubmittionContent 
                        transactionDetailsQuery={transactionDetailsQuery} 
                        filesQuery={filesQuery} 
                        token={token} 
                    />
                </ErrorBoundary>
            </PaymentMethodProvider>
        </FileProvider>
    );
};

const PaymentSubmittionContent = (props) => {
    const { transactionDetailsQuery, filesQuery, token } = props;
    const { t } = useTranslation();
    const { projectFullTitle, projectLogo } = useSettings();


    const transactionStatus = transactionDetailsQuery.data?.StatusId;
    const cardEnabled = transactionDetailsQuery.data?.CardEnabled;
    const bankEnabled = transactionDetailsQuery.data?.BankEnabled;
    // const selectedPaymentMethod = transactionDetailsQuery.data?.PaymentMethod;
    const { selectedPaymentMethod, handlePaymentMethodChange } = usePaymentMethod();

    useEffect(() => {
        console.log('transactionDetailsQuery.data?.InitPaymentOpt', transactionDetailsQuery.data?.InitPaymentOpt);
        handlePaymentMethodChange(transactionDetailsQuery.data?.InitPaymentOpt);
    }, [transactionDetailsQuery.data?.InitPaymentOpt]);

    const reCaptchaRef = useRef();

    const theme = useTheme();

    const navigate = useNavigate(); // Initialize navigate
    const { isLoggedIn } = useSelector((state) => state.auth);

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                pl: { xs: 2, sm: 4, md: 8, lg: 12 },
                pr: { xs: 2, sm: 4, md: 8, lg: 12 },
                width: '100%',
                borderRadius: 2
            }}
        >

            <Grid
                container
                spacing={1}
                alignItems="center"
                justifyContent="center"
                sx={{ p: 2, borderRadius: 2, background: theme.palette.background.paper }}
            >
                
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',  
                            width: '100%',
                            mb: 2
                        }}
                    >
                        {isLoggedIn && (
                            <Button 
                                variant="contained" 
                                color="primary" 
                                onClick={() => navigate(-1)}  
                                size="large"
                            >
                                {t('BacktoReceipt')}
                            </Button>
                        )}
                    </Box>

                <Grid item xs={12}>
                    <Stack spacing={1} direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h2">{projectFullTitle}</Typography>
                        <Logo logo={projectLogo} link="/" />
                    </Stack>
                </Grid>
                <Grid item xs={12}>
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Box sx={{ width: '4px', height: 'auto', background: colors?.primaryMain }} />
                        <Typography variant="h2">{t('PaymentPlatform')}</Typography>
                        <Typography variant="h2">{` - `}</Typography>
                        <Typography variant="h2">{t('PaymentRequests')}</Typography>
                    </Stack>
                </Grid>
                <Grid item xs={12} sx={{ mb: 1 }}>
                    <Divider />
                </Grid>
                <Grid item xs={12}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 1 }}>
                        {transactionDetailsQuery.data?.TransNumber && (
                            <Typography variant="h2">
                                {`${t('TransactionNumber')}  #${transactionDetailsQuery.data?.TransNumber}`}
                            </Typography>
                        )}
                        <Typography variant="subtitle1">{transactionDetailsQuery.data?.OrganizationAbbr}</Typography>
                        {transactionDetailsQuery.data?.TransDate && (
                            <Typography variant="subtitle1">
                                {format(new Date(transactionDetailsQuery.data?.TransDate), 'dd/MM/yyyy')}
                            </Typography>
                        )}
                        <Spacer />
                        <Chip size="medium" label={transactionDetailsQuery.data?.StatusAbbr} />
                    </Stack>
                </Grid>
                <Grid item xs={12}>
                    {transactionStatus && <StatusAlert status={transactionStatus} />}
                </Grid>
                <Grid item xs={12}>
                    <Grid item xs={12}>
                        {transactionDetailsQuery.data?.Details && <PaymentDetails details={transactionDetailsQuery.data?.Details ?? []} />}
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    {transactionStatus !== 420 ? (
                        <PaymentMethodSelector transactionStatus={transactionStatus} cardEnabled={cardEnabled} bankEnabled={bankEnabled} />
                    ) : null}
                </Grid>
                
                <Grid item xs={12}>
                    {
                        {
                            [PaymentMethod.bank.id]: (
                                <PaymentMethodBank
                                    transactionDetailsQuery={transactionDetailsQuery}
                                    filesQuery={filesQuery}
                                    token={token}
                                />
                            ),
                            [PaymentMethod.card.id]: <PaymentMethodCard transactionDetailsQuery={transactionDetailsQuery} token={token} />
                        }[selectedPaymentMethod]
                    }
                </Grid>

                <ReCAPTCHA sitekey={recaptchaKey} size="invisible" ref={reCaptchaRef} style={{ visibility: 'hidden' }} />
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',  
                        width: '100%',
                        mb: 2
                    }}
                > 
                    {isLoggedIn && (
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={() => navigate(-1)}  
                            size="large"
                        >
                            {t('BacktoReceipt')}
                        </Button>
                    )}
                </Box>
            </Grid>
        </Box>
    );
};

export default PaymentSubmittion;
